import axios, { AxiosRequestConfig, AxiosResponse, AxiosProgressEvent, ResponseType } from 'axios';
import { getCookies, getToken, logoutUser, setCookies } from '../utility/helper';
import { API_ENDPOINT, API_URL } from './constant';

interface CallAPIOptions {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'; // Restrict to common HTTP methods
    data?: any; // Data to be sent with the request
    multipart?: boolean; // Flag to handle multipart/form-data
    responseType?: ResponseType; //to download a blob
    progressEvent?: (progressEvent: AxiosProgressEvent) => void; // Progress event handler for uploads
}

let urls = ['login', 'forgot', 'sign-up', 'reset-password'];

/**
 * This function calls APIs via axios.
 *
 * @param {string} url - The API endpoint URL.
 * @param {CallAPIOptions} options - API options like method, data, and multipart flag.
 * @returns {Promise<any>} - The API response.
 * @throws {Error} - Throws an error if the API call fails.
 */
export const callApi = async <T = any>(
    url: string,
    options: CallAPIOptions
): Promise<T> => {
    let token = getToken();

    let requestData: any;

    if (options?.data) {
        requestData = options.multipart ? options.data : JSON.stringify(options.data);
    } else {
        requestData = undefined;
    }

    const axiosConfig: AxiosRequestConfig = {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": options?.multipart ? "multipart/form-data" : "application/json",
        },
        method: options.method,
        data: requestData,
        responseType: options.responseType || 'json', // Default to JSON
        ...(options.multipart &&
            options.progressEvent && { onUploadProgress: options.progressEvent }),
    };

    try {
        const response: AxiosResponse<T> = await axios(url, axiosConfig);
        if (response.status === 200 || response.status === 201) {
            return response.data;
        } else {
            throw new Error(response.data as unknown as string);
        }
    } catch (error: any) {
        return handleApiError<T>(error, url, axiosConfig);
    }
};


const handleApiError = async <T>(
    error: any,
    url: string,
    axiosConfig: AxiosRequestConfig
): Promise<T> => {
    // Check for unauthorized error and ensure the URL does not include excluded keywords
    if (error.response?.status === 401 && !urls.some(keyword => url.includes(keyword))) {
        try {
            // Refresh the token and update the authorization header
            const newToken = await refreshToken();
            axiosConfig.headers = {
                ...axiosConfig.headers,
                Authorization: `Bearer ${newToken}`,
            };
            const retryResponse: AxiosResponse<T> = await axios(url, axiosConfig);
            return retryResponse.data;
        } catch (refreshError: any) {
            throw new Error(refreshError.message || "Token refresh failed.");
        }
    }
    throw new Error(error.response?.data?.message || "An unknown error occurred.");
};

const refreshToken = async (): Promise<string> => {
    try {
        const token = getCookies(import.meta.env.VITE_BUSINESS + "deviceToken");
        const response: any = await axios.post(
            API_URL + API_ENDPOINT.auth + "user/refresh-token",
            {
                refreshToken: getCookies(
                    import.meta.env.VITE_BUSINESS + "refreshToken"
                ),
                deviceToken: token || 'unique-device-token'
            }
        );
        const newToken = response.data.accessToken;
        setCookies(import.meta.env.VITE_BUSINESS + "token", newToken);
        return newToken;
    } catch (error: any) {
        if (error.response?.status === 401) {
            logoutUser();
        }
        throw new Error("Unable to refresh token. Please log in again.");
    }
};
